import { useEffect, useState } from "react";

import {
  type ApiDeps,
  type InvoiceDrilldown,
  type InvoiceListItem,
  type InvoiceStatusFilter,
  type InvoiceTarget,
  fetchInvoiceDetail,
  fetchInvoices,
} from "../lib/api";
import { t, tn, type Language, type StringKey } from "../lib/i18n";
import { formatMoneyParts } from "../lib/money";
import { CorrelationId } from "./CorrelationId";
import { Icon } from "./icons";
import { InvoiceResult, InvoiceSearch } from "./InvoiceSearch";

interface InvoiceListProps {
  deps: ApiDeps;
  language: Language;
  /** Rolling window length threaded from the RangePicker. */
  periodDays: number;
  /** Shared refresh signal — bump to refetch the first page. */
  reloadKey?: number;
  /** Navigation target from Overview (KPI tile → status; Needs-you CTA →
   * correlationId drill-in). `seq` bumps even when the target repeats so
   * the effect re-applies it. */
  nav?: { target: InvoiceTarget; seq: number } | null;
}

const PAGE_LIMIT = 50;

const STATUS_SEGMENTS: ReadonlyArray<{
  value: InvoiceStatusFilter;
  labelKey: StringKey;
}> = [
  { value: "all", labelKey: "invoices.filter.all" },
  { value: "filed", labelKey: "invoices.filter.filed" },
  { value: "review", labelKey: "invoices.filter.review" },
];

type ListState =
  | { kind: "loading" }
  | { kind: "loaded"; items: InvoiceListItem[]; nextCursor: string | null }
  | { kind: "error" };

/** Square data-layer status badge for one row. Both tints carry a darkened
 * companion ink (`successText` / `warnText`) — the shipped pairing was
 * 2.7:1 against a 30%-opacity tint (design review finding 1). */
function StatusBadge({
  status,
  language,
}: {
  status: InvoiceListItem["status"];
  language: Language;
}): JSX.Element {
  const filed = status === "filed";
  return (
    <span
      className={
        "inline-block rounded-[2px] px-1.5 py-0.5 text-xs font-semibold " +
        (filed
          ? "bg-kwanza-successTint text-kwanza-successText"
          : "bg-kwanza-warnTint text-kwanza-warnText")
      }
      data-testid={`invoice-status-${status}`}
    >
      {t(filed ? "invoices.status.filed" : "invoices.status.review", language)}
    </span>
  );
}

/** Money cell — muted symbol, bold tabular figure. Tabular numerals matter
 * here specifically: this column is right-aligned and scanned vertically,
 * which is the whole reason `Kz 12500.00` vs `Kz 1250.00` was a real
 * misread risk before the amounts were grouped (findings 5 + 6). */
function Amount({
  money,
  language,
}: {
  money: InvoiceListItem["amount"];
  language: Language;
}): JSX.Element {
  const parts = formatMoneyParts(money, language);
  if (!parts) {
    return <span className="text-kwanza-inkSoft">—</span>;
  }
  return (
    <span className="whitespace-nowrap tabular-nums" data-testid="invoice-amount">
      <span className="text-kwanza-inkSoft">{parts.symbol}</span>{" "}
      <span className="font-bold text-kwanza-ink">{parts.value}</span>
    </span>
  );
}

/** Drill-in detail for one row — reuses the reference-search detail body
 * (InvoiceResult + ReasoningTrace) so the two entry points never drift. */
function InvoiceDrillIn({
  deps,
  language,
  correlationId,
  onBack,
}: {
  deps: ApiDeps;
  language: Language;
  correlationId: string;
  onBack: () => void;
}): JSX.Element {
  const [state, setState] = useState<
    | { kind: "loading" }
    | { kind: "loaded"; result: InvoiceDrilldown }
    | { kind: "error" }
  >({ kind: "loading" });

  useEffect(() => {
    let cancelled = false;
    setState({ kind: "loading" });
    fetchInvoiceDetail(deps, correlationId)
      .then((result) => {
        if (!cancelled) setState({ kind: "loaded", result });
      })
      .catch(() => {
        if (!cancelled) setState({ kind: "error" });
      });
    return () => {
      cancelled = true;
    };
  }, [deps.backendUrl, deps.getAuthToken, correlationId]);

  return (
    <section
      aria-label={t("invoices.list.heading", language)}
      className="rounded-[2px] border border-kwanza-divider bg-kwanza-card p-4 shadow-sm"
    >
      <button
        type="button"
        onClick={onBack}
        data-testid="invoice-back"
        className="mb-3 inline-flex items-center gap-1 text-sm font-medium text-kwanza-accentText hover:underline"
      >
        <span aria-hidden="true" className="inline-block rotate-180">
          <Icon name="chevron-right" size={15} />
        </span>
        {t("invoices.back", language)}
      </button>

      {state.kind === "loading" ? (
        <p className="text-sm text-kwanza-inkSoft" aria-busy="true">
          {t("loading", language)}
        </p>
      ) : state.kind === "error" ? (
        <p className="text-sm text-kwanza-alert" data-testid="invoice-drill-error">
          {t("search.error.body", language)}
        </p>
      ) : (
        <>
          <div className="flex items-center gap-2">
            <span className="text-xs uppercase tracking-wide text-kwanza-inkSoft">
              {t("reference.label", language)}
            </span>
            <CorrelationId value={correlationId} language={language} />
          </div>
          <InvoiceResult result={state.result} language={language} />
        </>
      )}
    </section>
  );
}

/** Browsable, filterable invoice list (F37 §12.6, Task 5) — the primary
 * invoice entry point that replaces "paste a correlation_id". A status
 * segmented control (backend filter) + a client-side vendor/number text
 * search over the loaded page + rows leading with vendor · number · date ·
 * amount · status; a row click drills into the existing detail render. The
 * old reference search is demoted to a disclosure at the bottom. Universal —
 * no tenant copy. */
export function InvoiceList({
  deps,
  language,
  periodDays,
  reloadKey = 0,
  nav = null,
}: InvoiceListProps): JSX.Element {
  const [status, setStatus] = useState<InvoiceStatusFilter>("all");
  const [search, setSearch] = useState("");
  const [state, setState] = useState<ListState>({ kind: "loading" });
  const [loadingMore, setLoadingMore] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);
  const [refOpen, setRefOpen] = useState(false);

  // Apply an Overview navigation target (KPI tile status / Needs-you drill).
  useEffect(() => {
    if (!nav) return;
    if (nav.target.correlationId) {
      setSelected(nav.target.correlationId);
    } else if (nav.target.status) {
      setSelected(null);
      setStatus(nav.target.status);
    }
    // Keyed on nav.seq so the same target re-applies on a repeat click.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nav?.seq]);

  // First-page fetch — refires on status / period / refresh change.
  useEffect(() => {
    let cancelled = false;
    setState({ kind: "loading" });
    fetchInvoices(deps, { status, period_days: periodDays, limit: PAGE_LIMIT })
      .then((page) => {
        if (!cancelled)
          setState({
            kind: "loaded",
            items: page.items,
            nextCursor: page.next_cursor,
          });
      })
      .catch(() => {
        if (!cancelled) setState({ kind: "error" });
      });
    return () => {
      cancelled = true;
    };
  }, [deps.backendUrl, deps.getAuthToken, status, periodDays, reloadKey]);

  const loadMore = (): void => {
    if (state.kind !== "loaded" || state.nextCursor === null || loadingMore) {
      return;
    }
    setLoadingMore(true);
    fetchInvoices(deps, {
      status,
      period_days: periodDays,
      limit: PAGE_LIMIT,
      cursor: state.nextCursor,
    })
      .then((page) => {
        setState((prev) =>
          prev.kind === "loaded"
            ? {
                kind: "loaded",
                items: [...prev.items, ...page.items],
                nextCursor: page.next_cursor,
              }
            : prev,
        );
      })
      .catch(() => {
        /* keep what's shown; load-more can be retried */
      })
      .finally(() => setLoadingMore(false));
  };

  if (selected !== null) {
    return (
      <InvoiceDrillIn
        deps={deps}
        language={language}
        correlationId={selected}
        onBack={() => setSelected(null)}
      />
    );
  }

  const allItems = state.kind === "loaded" ? state.items : [];
  const q = search.trim().toLowerCase();
  const filtered = q
    ? allItems.filter(
        (i) =>
          (i.vendor_display_name ?? "").toLowerCase().includes(q) ||
          (i.vendor_slug ?? "").toLowerCase().includes(q) ||
          (i.invoice_number ?? "").toLowerCase().includes(q),
      )
    : allItems;

  return (
    <section
      aria-label={t("invoices.list.heading", language)}
      className="rounded-[2px] border border-kwanza-divider bg-kwanza-card p-4 shadow-sm"
    >
      <div className="mb-3 flex items-center justify-between gap-3">
        <h2 className="text-base font-semibold text-kwanza-ink">
          {t("invoices.list.heading", language)}
        </h2>
        {/* Say what the number IS (design review finding 8). Both figures
         * came from the loaded page, so "30 de 30" read as an authoritative
         * window total while the text box only ever searched what had been
         * fetched — load-more then re-search and the same query returned
         * more rows than a second earlier. */}
        {state.kind === "loaded" ? (
          <span className="text-xs text-kwanza-inkSoft" data-testid="invoice-count">
            {tn("invoices.count_loaded", { n: filtered.length }, language)}
            {state.nextCursor !== null
              ? ` ${t("invoices.count_more", language)}`
              : ""}
          </span>
        ) : null}
      </div>

      <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2 rounded-lg border border-kwanza-divider px-2 py-1.5 sm:min-w-0 sm:flex-1">
          <Icon name="search" size={15} className="text-kwanza-inkSoft" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("invoices.search_placeholder", language)}
            aria-label={t("invoices.search_label", language)}
            data-testid="invoice-search-box"
            className="w-full bg-transparent text-sm text-kwanza-ink outline-none placeholder:text-kwanza-inkSoft"
          />
        </div>
        <div
          role="group"
          aria-label={t("invoices.filter_label", language)}
          className="flex flex-wrap items-center gap-1"
          data-testid="invoice-status-filter"
        >
          {STATUS_SEGMENTS.map((seg) => {
            const active = status === seg.value;
            return (
              <button
                key={seg.value}
                type="button"
                onClick={() => setStatus(seg.value)}
                aria-pressed={active}
                data-testid={`invoice-filter-${seg.value}`}
                className={
                  "rounded-md px-2.5 py-1 text-xs " +
                  (active
                    ? "bg-kwanza-accent font-bold text-kwanza-onAccent"
                    : "border border-kwanza-divider bg-kwanza-card font-medium text-kwanza-inkSoft hover:text-kwanza-ink")
                }
              >
                {t(seg.labelKey, language)}
              </button>
            );
          })}
        </div>
      </div>

      {state.kind === "loading" ? (
        <ul aria-busy="true" data-testid="invoice-skeleton">
          {[0, 1, 2, 3].map((i) => (
            <li
              key={i}
              className="mb-2 h-10 animate-pulse rounded-[2px] bg-kwanza-divider"
            />
          ))}
        </ul>
      ) : state.kind === "error" ? (
        <p className="text-sm text-kwanza-alert" data-testid="invoice-list-error">
          {t("error.fetch_failed.body", language)}
        </p>
      ) : filtered.length === 0 ? (
        <p className="text-sm text-kwanza-inkSoft" data-testid="invoice-empty">
          {t("invoices.empty", language)}
        </p>
      ) : (
        /* Two-line under 640px, one line above it — same DOM, no column
         * hidden, no horizontal scroll (design review finding 6 + mockup
         * 2D-3). The shipped row put `hidden sm:inline` on BOTH the number
         * and the date, so on a Teams phone tab two Unitel invoices in the
         * same month were indistinguishable and "did invoice X get filed?"
         * could only be answered by opening rows one at a time. */
        <ul className="divide-y divide-kwanza-divider" data-testid="invoice-rows">
          {filtered.map((inv) => (
            <li key={`${inv.correlation_id}-${inv.status}`}>
              <button
                type="button"
                onClick={() => setSelected(inv.correlation_id)}
                data-testid="invoice-row"
                className="flex w-full items-center gap-3 py-2 text-left hover:bg-kwanza-surface"
              >
                <span className="grid min-w-0 flex-1 grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-1 sm:grid-cols-[minmax(0,1fr)_6rem_6rem_7rem_5rem]">
                  <span className="col-start-1 row-start-1 min-w-0 truncate text-sm font-semibold text-kwanza-ink">
                    {inv.vendor_display_name ?? inv.vendor_slug ?? "—"}
                  </span>
                  {/* `sm:contents` dissolves this wrapper into the grid above
                   * 640px, so the same three nodes are a second line on a
                   * phone and three columns on a tab — one DOM, both shapes. */}
                  <span className="col-span-2 row-start-2 flex items-baseline gap-2 text-xs text-kwanza-inkSoft sm:contents">
                    <span className="min-w-0 truncate sm:col-start-2 sm:row-start-1">
                      {inv.invoice_number ?? "—"}
                    </span>
                    <span aria-hidden="true" className="sm:hidden">
                      ·
                    </span>
                    <span className="whitespace-nowrap sm:col-start-3 sm:row-start-1">
                      {inv.invoice_date ?? "—"}
                    </span>
                    <span className="ml-auto text-sm sm:col-start-4 sm:row-start-1 sm:ml-0 sm:text-right">
                      <Amount money={inv.amount} language={language} />
                    </span>
                  </span>
                  <span className="col-start-2 row-start-1 justify-self-end sm:col-start-5">
                    <StatusBadge status={inv.status} language={language} />
                  </span>
                </span>
                <span aria-hidden="true" className="shrink-0 text-kwanza-inkSoft">
                  <Icon name="chevron-right" size={15} />
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {state.kind === "loaded" && state.nextCursor !== null ? (
        <div className="mt-3">
          <button
            type="button"
            onClick={loadMore}
            disabled={loadingMore}
            data-testid="invoice-load-more"
            className="rounded-md border border-kwanza-divider bg-kwanza-surface px-3 py-1 text-sm font-medium text-kwanza-ink hover:bg-kwanza-accentSoft disabled:opacity-60"
          >
            {loadingMore
              ? t("atividade.loading_more", language)
              : t("atividade.load_more", language)}
          </button>
        </div>
      ) : null}

      {/* Demoted reference search — power-user adjunct, not the primary
       * entry point (was the old InvoiceSearch landing). */}
      <div className="mt-4 border-t border-kwanza-divider pt-3">
        <button
          type="button"
          onClick={() => setRefOpen((v) => !v)}
          aria-expanded={refOpen}
          data-testid="invoice-ref-disclosure"
          className="inline-flex items-center gap-1 text-xs text-kwanza-inkSoft hover:text-kwanza-ink"
        >
          <Icon name="search" size={13} />
          {t("invoices.ref_disclosure", language)}
        </button>
        {refOpen ? (
          <div className="mt-3" data-testid="invoice-ref-search">
            <InvoiceSearch deps={deps} language={language} />
          </div>
        ) : null}
      </div>
    </section>
  );
}
